import type { ReactNode } from "react";
import { Dialog as AriaDialog, Modal as AriaModal, ModalOverlay } from "react-aria-components";
import { cx } from "@/lib/cx";

export { DialogHeader as DrawerHeader } from "./Modal";

const sizes = { md: "w-[min(480px,100vw)]", lg: "w-[min(600px,100vw)]" } as const;

/**
 * Detail sheet that slides in from the right edge over a scrim, for "open a row and edit it" flows
 * (admin users, groups, roles). React Aria traps focus, closes on Escape and returns focus to the row
 * that opened it. Compose: <Drawer><DrawerHeader/><DrawerBody/><DrawerFooter/></Drawer>.
 * The header's title is the dialog's accessible name.
 */
export function Drawer({
  isOpen,
  onOpenChange,
  size = "md",
  children,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  size?: keyof typeof sizes;
  children: ReactNode | ((opts: { close: () => void }) => ReactNode);
}) {
  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      isDismissable
      className="fixed inset-0 z-50 flex items-stretch justify-end bg-scrim anim-fade"
    >
      <AriaModal
        className={cx(
          "anim-drawer-right h-full max-w-full border-l border-popover-border bg-dialog-bg text-fg shadow-lg outline-none",
          sizes[size],
        )}
      >
        <AriaDialog className="flex h-full min-h-0 flex-col outline-none">{children}</AriaDialog>
      </AriaModal>
    </ModalOverlay>
  );
}

/** Scrolling body of a drawer: sections separated by hairlines. */
export function DrawerBody({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cx("scroll-thin min-h-0 flex-1 overflow-auto px-5 pt-1 pb-5 text-sm text-fg", className)}>
      {children}
    </div>
  );
}

export function DrawerFooter({ children }: { children: ReactNode }) {
  return (
    <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-line px-5 py-3">
      {children}
    </div>
  );
}

/** A titled block inside DrawerBody. The first one has no top rule. */
export function DrawerSection({
  title,
  description,
  actions,
  children,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3 border-t border-line py-5 first:border-t-0 first:pt-3">
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold tracking-snug text-fg">{title}</h3>
          {description ? <p className="mt-0.5 text-xs text-pretty text-fg-muted">{description}</p> : null}
        </div>
        {actions}
      </header>
      {children}
    </section>
  );
}
